import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../auth/AuthContext";
import { GIVEAWAY_EVENT_SLUG } from "../../lib/giveawayCampaign";
import { toast } from "sonner";
import { Sparkles, Ticket, Trash2, Trophy } from "lucide-react";
import { Switch } from "../../components/ui/switch";

export default function AdminGiveawayTab({
  entries,
  purchasers,
  campaignEnabled,
  campaignUpdatedAt,
  onRefresh,
  onDrawComplete,
}) {
  const [ticketCount, setTicketCount] = useState("1");
  const [purchaserName, setPurchaserName] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [adding, setAdding] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [lastDraw, setLastDraw] = useState(null);
  const [togglingCampaign, setTogglingCampaign] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [clearPurchasers, setClearPurchasers] = useState(true);
  const [clearEntries, setClearEntries] = useState(true);
  const [clearDraws, setClearDraws] = useState(true);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [cleaningUp, setCleaningUp] = useState(false);

  const base = `${API}/admin/giveaway/${GIVEAWAY_EVENT_SLUG}`;

  const loadCleanupPreview = async () => {
    try {
      const r = await axios.get(`${base}/cleanup-preview`, { withCredentials: true });
      setCleanupPreview(r.data);
    } catch {
      setCleanupPreview(null);
    }
  };

  const toggleCampaign = async (next) => {
    setTogglingCampaign(true);
    try {
      await axios.patch(`${base}/settings`, { enabled: next }, { withCredentials: true });
      toast.success(next ? "Campaign is live on zcars.io" : "Campaign hidden from zcars.io");
      onRefresh?.();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Could not update campaign.");
    } finally {
      setTogglingCampaign(false);
    }
  };

  const addPurchaser = async (e) => {
    e.preventDefault();
    const trimmedCount = String(ticketCount ?? "").trim();
    const count = trimmedCount === "" ? 1 : parseInt(trimmedCount, 10);
    if (!purchaserName.trim() || !Number.isFinite(count) || count < 1) {
      toast.error("Purchaser name required; ticket count must be 1 or more (default 1).");
      return;
    }
    setAdding(true);
    try {
      await axios.post(
        `${base}/ticket-purchasers`,
        { purchaser_name: purchaserName.trim(), ticket_count: count },
        { withCredentials: true },
      );
      toast.success("Purchaser allocation saved.");
      setTicketCount("1");
      setPurchaserName("");
      onRefresh?.();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Could not add purchaser.");
    } finally {
      setAdding(false);
    }
  };

  const parseBulk = () => {
    const lines = bulkText.split(/\n/).map((l) => l.trim()).filter(Boolean);
    const rows = [];
    for (const line of lines) {
      const parts = (line.includes(",") ? line.split(",") : line.split(/\t/))
        .map((p) => p.trim())
        .filter(Boolean);
      if (!parts.length) continue;
      const last = parts[parts.length - 1];
      if (/^\d+$/.test(last) && parts.length >= 2) {
        rows.push({
          purchaser_name: parts.slice(0, -1).join(", "),
          ticket_count: parseInt(last, 10),
        });
      } else if (/^\d+$/.test(parts[0]) && parts.length >= 2) {
        rows.push({
          purchaser_name: parts.slice(1).join(", "),
          ticket_count: parseInt(parts[0], 10),
        });
      } else {
        rows.push({ purchaser_name: parts.join(", "), ticket_count: 1 });
      }
    }
    return rows;
  };

  const bulkImport = async () => {
    const rows = parseBulk();
    if (!rows.length) {
      toast.error("Paste rows as: name, ticket count (one per line).");
      return;
    }
    setAdding(true);
    try {
      const r = await axios.post(
        `${base}/ticket-purchasers/bulk`,
        { rows },
        { withCredentials: true },
      );
      toast.success(`Imported ${r.data?.inserted ?? 0} tickets (${r.data?.skipped ?? 0} skipped).`);
      setBulkText("");
      onRefresh?.();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Bulk import failed.");
    } finally {
      setAdding(false);
    }
  };

  const runDraw = async () => {
    if (!window.confirm("Run the AI-assisted raffle draw now? This marks one entry as winner.")) return;
    setDrawing(true);
    try {
      const r = await axios.post(`${base}/draw`, { use_ai_announcement: true }, { withCredentials: true });
      setLastDraw(r.data);
      onDrawComplete?.(r.data);
      toast.success(`Winner: ${r.data?.winner?.full_name || "—"}`);
      onRefresh?.();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Draw failed.");
    } finally {
      setDrawing(false);
    }
  };

  const entryList = entries || [];
  const purchaserList = purchasers || [];

  useEffect(() => {
    loadCleanupPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignEnabled, entryList.length, purchaserList.length]);

  const runCleanup = async () => {
    if (campaignEnabled) {
      toast.error("Turn the campaign off before clearing test data.");
      return;
    }
    if (!clearPurchasers && !clearEntries && !clearDraws) {
      toast.error("Select at least one dataset to clear.");
      return;
    }
    const required = cleanupPreview?.confirm_phrase_required || "RESET GIVEAWAY";
    if (confirmPhrase.trim() !== required) {
      toast.error(`Type ${required} to confirm.`);
      return;
    }
    if (
      !window.confirm(
        "This permanently deletes the selected giveaway data for Apna Caledon. Continue?",
      )
    ) {
      return;
    }
    setCleaningUp(true);
    try {
      const r = await axios.post(
        `${base}/cleanup`,
        {
          confirm_phrase: confirmPhrase.trim(),
          clear_purchasers: clearPurchasers,
          clear_entries: clearEntries,
          clear_draws: clearDraws,
        },
        { withCredentials: true },
      );
      const d = r.data?.deleted || {};
      toast.success(
        `Cleared ${d.purchasers ?? 0} tickets, ${d.entries ?? 0} entries, ${d.draws ?? 0} draws.`,
      );
      setConfirmPhrase("");
      setLastDraw(null);
      await loadCleanupPreview();
      onRefresh?.();
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Cleanup failed.");
    } finally {
      setCleaningUp(false);
    }
  };

  const counts = cleanupPreview?.counts || {
    purchasers: purchaserList.length,
    entries: entryList.length,
    draws: 0,
  };

  return (
    <div className="mt-4 space-y-6" data-testid="admin-giveaway-tab">
      <div
        className="zc-card p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        data-testid="admin-giveaway-campaign-toggle"
      >
        <div>
          <div className="eyebrow mb-1">Campaign visibility</div>
          <p className="text-sm text-[var(--zc-text-2)] max-w-xl">
            When off, the home page banner and public entry form are hidden. Admin tools stay available.
          </p>
          {campaignUpdatedAt && (
            <p className="text-xs text-[var(--zc-text-3)] mt-2">
              Last changed {new Date(campaignUpdatedAt).toLocaleString()}
            </p>
          )}
        </div>
        <label className="flex items-center gap-3 shrink-0 cursor-pointer">
          <span className="text-sm font-semibold text-[var(--zc-text)]">
            {campaignEnabled ? "Live" : "Off"}
          </span>
          <Switch
            checked={campaignEnabled === true}
            disabled={togglingCampaign || campaignEnabled === null}
            onCheckedChange={toggleCampaign}
            data-testid="admin-giveaway-campaign-switch"
            className="data-[state=checked]:bg-[var(--zc-accent)]"
          />
        </label>
      </div>

      <div className="zc-card p-5 border border-[var(--zc-border-2)]" data-testid="admin-giveaway-cleanup">
        <div className="flex items-center gap-2 mb-2">
          <Trash2 size={16} className="text-[var(--zc-danger)]" />
          <span className="eyebrow">Pre-go-live cleanup</span>
        </div>
        <p className="text-sm text-[var(--zc-text-2)] max-w-2xl">
          Wipe test tickets, practice entries, or trial draws before opening the campaign. Campaign must
          be <strong>Off</strong>. Then re-import your real ticket list and turn Live.
        </p>
        <div className="flex flex-wrap gap-4 mt-4 text-sm text-[var(--zc-text)]">
          <span>{counts.purchasers} buyer rows</span>
          <span>{counts.entries} entries</span>
          <span>{counts.draws} draws</span>
        </div>
        {campaignEnabled && (
          <p className="text-sm mt-3" style={{ color: "var(--zc-danger)" }}>
            Turn campaign Off above before cleanup.
          </p>
        )}
        <div className="grid sm:grid-cols-3 gap-3 mt-4 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={clearPurchasers}
              onChange={(e) => setClearPurchasers(e.target.checked)}
              data-testid="admin-giveaway-clear-purchasers"
            />
            Paid ticket registry
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={clearEntries}
              onChange={(e) => setClearEntries(e.target.checked)}
              data-testid="admin-giveaway-clear-entries"
            />
            Enrolled entries
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={clearDraws}
              onChange={(e) => setClearDraws(e.target.checked)}
              data-testid="admin-giveaway-clear-draws"
            />
            Draw history
          </label>
        </div>
        <label className="block mt-4 text-sm text-[var(--zc-text-2)]" htmlFor="giveaway-cleanup-confirm">
          Type <span className="font-mono font-semibold text-[var(--zc-text)]">RESET GIVEAWAY</span> to confirm
        </label>
        <input
          id="giveaway-cleanup-confirm"
          className="zc-input w-full max-w-md mt-1.5"
          value={confirmPhrase}
          onChange={(e) => setConfirmPhrase(e.target.value)}
          autoComplete="off"
          placeholder="RESET GIVEAWAY"
          data-testid="admin-giveaway-cleanup-confirm"
        />
        <button
          type="button"
          onClick={runCleanup}
          disabled={cleaningUp || campaignEnabled}
          className="mt-4 px-5 h-10 zc-btn-ghost text-xs border border-[var(--zc-danger)] text-[var(--zc-danger)]"
          data-testid="admin-giveaway-cleanup-run"
        >
          {cleaningUp ? "Clearing…" : "Clear selected data"}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="zc-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Ticket size={16} className="text-[var(--zc-secondary)]" />
            <span className="eyebrow">Paid ticket registry</span>
          </div>
          <p className="text-sm text-[var(--zc-text-2)] mb-4">
            Register who paid — and how many tickets (group buys). Entrants only validate by purchaser name;
            each enrollment uses one slot until the count reaches zero.
          </p>
          <form onSubmit={addPurchaser} className="space-y-3">
            <input
              className="zc-input w-full"
              placeholder="Purchaser name"
              value={purchaserName}
              onChange={(e) => setPurchaserName(e.target.value)}
              data-testid="admin-giveaway-purchaser-name"
            />
            <input
              className="zc-input w-full"
              type="number"
              min={1}
              placeholder="Tickets purchased"
              value={ticketCount}
              onChange={(e) => setTicketCount(e.target.value)}
              data-testid="admin-giveaway-ticket-count"
            />
            <button type="submit" className="zc-btn-secondary w-full h-10 text-xs" disabled={adding}>
              Add / increase allocation
            </button>
          </form>
          <textarea
            className="zc-input w-full mt-4 min-h-[100px] text-sm font-mono"
            placeholder={"Bulk paste:\nSankha Nath, 5\nJane Doe, 1"}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            data-testid="admin-giveaway-bulk"
          />
          <button
            type="button"
            onClick={bulkImport}
            className="zc-btn-outline-secondary w-full h-10 text-xs mt-2"
            disabled={adding}
          >
            Import bulk
          </button>
          <p className="text-xs text-[var(--zc-text-3)] mt-3">{purchaserList.length} purchaser(s) in registry</p>
        </div>

        <div className="zc-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-[var(--zc-accent)]" />
            <span className="eyebrow">AI raffle draw</span>
          </div>
          <p className="text-sm text-[var(--zc-text-2)] mb-4">
            Winner is picked with a cryptographically secure random draw from enrolled entries. Gemini
            generates the MC announcement script (optional).
          </p>
          <button
            type="button"
            onClick={runDraw}
            className="zc-btn-primary w-full h-11 text-sm flex items-center justify-center gap-2"
            disabled={drawing}
            data-testid="admin-giveaway-draw"
          >
            <Trophy size={16} />
            {drawing ? "Drawing…" : 'Draw 65" TV winner'}
          </button>
          {lastDraw?.draw?.announcement && (
            <div className="mt-4 p-4 rounded-[12px] bg-[var(--zc-bg-3)] text-sm text-[var(--zc-text)] whitespace-pre-wrap">
              {lastDraw.draw.announcement}
            </div>
          )}
          {lastDraw?.winner && (
            <div className="mt-3 text-xs text-[var(--zc-text-2)]">
              Winner: <b>{lastDraw.winner.full_name}</b> · {lastDraw.winner.email} · ticket{" "}
              {lastDraw.winner.ticket_serial}
            </div>
          )}
        </div>
      </div>

      <div className="zc-card overflow-hidden">
        <div className="px-4 sm:px-6 py-3 border-b border-[var(--zc-border)] eyebrow">
          Purchaser allocations ({purchaserList.length})
        </div>
        {purchaserList.length === 0 && (
          <div className="px-6 py-8 text-center text-sm text-[var(--zc-text-3)]">No purchasers yet.</div>
        )}
        {purchaserList.map((row) => (
          <div
            key={row.purchase_record_id}
            className="px-4 sm:px-6 py-3 border-b border-[var(--zc-border)] last:border-0 text-sm flex flex-wrap justify-between gap-2"
          >
            <span className="font-semibold">{row.purchaser_name}</span>
            <span className="text-[var(--zc-text-2)]">
              {row.tickets_used ?? 0} / {row.tickets_total ?? row.ticket_count ?? 1} used ·{" "}
              <span className="text-[var(--zc-accent)] font-semibold">
                {row.tickets_remaining ?? "—"} left
              </span>
            </span>
          </div>
        ))}
      </div>

      <div className="zc-card overflow-hidden">
        <div className="px-4 sm:px-6 py-3 border-b border-[var(--zc-border)] eyebrow">
          Enrolled entries ({entryList.length})
        </div>
        {entryList.length === 0 && (
          <div className="px-6 py-10 text-center text-sm text-[var(--zc-text-3)]">No entries yet.</div>
        )}
        {entryList.map((row) => (
          <div
            key={row.entry_id}
            className="px-4 sm:px-6 py-3 border-b border-[var(--zc-border)] last:border-0 text-sm grid sm:grid-cols-2 gap-1"
          >
            <div>
              <span className="font-semibold">{row.full_name}</span>
              <span className="text-[var(--zc-text-3)]"> · ticket {row.ticket_serial}</span>
            </div>
            <div className="text-[var(--zc-text-2)] sm:text-right">
              {row.email} · {row.mobile}
              {row.status === "won" && (
                <span className="ml-2 text-[var(--zc-accent)] font-semibold uppercase text-xs">Winner</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
